import { XIcon } from '@heroicons/react/solid';
import axios from 'axios';
import React, { useEffect, useRef, useState } from 'react';
import { FaMapMarkerAlt } from 'react-icons/fa';
import styled from 'styled-components';
import { t } from 'ttag';

import { useOutsideClick } from '../../../hooks/useOutsideClick';
import { ButtonPrimaryMedium } from '../Button/Button';
import Input from '../Input/Input';

const PlaceSearchWrap = styled.div`
  position: relative;
  .place-search-search-area {
    display: flex;
    width: 100%;
    align-items: ${(props) => (props.error ? 'center' : 'flex-end')};
    justify-content: flex-start;
  }
  .place-search-results {
    position: absolute;
    background: white;
    z-index: 999;
    box-shadow: ${({ theme }) => theme.boxShadow};
    padding: ${({ theme }) => theme.spacing02} ${({ theme }) => theme.spacing02};
    width: 100%;
    top: calc(100% + 8px);
  }

  .input-primary {
    flex-grow: 1;
  }

  .place-search-input-area {
    margin-right: ${({ theme }) => theme.spacing01};
  }

  .field-icon-wrap {
    margin-right: ${({ theme }) => theme.spacing01};
    height: ${({ theme }) => theme.spacing05};
    width: ${({ theme }) => theme.spacing05};
    border: 1px solid ${({ theme }) => theme.colorUI30};
    background: 1px solid ${({ theme }) => theme.colorUI20};
    border-radius: ${({ theme }) => theme.borderRadius};
    display: flex;
    align-items: center;
    background: white;
    justify-content: center;
    flex-shrink: 0;
  }

  .place-search-results {
    position: absolute;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    &-exit-icon {
      margin-bottom: ${({ theme }) => theme.spacing01};
      flex-grow: 0;
      width: 24px;
      height: 24px;
      border: none;
      border-radius: ${({ theme }) => theme.borderRadius};
      background: none;
      :hover {
        background: ${({ theme }) => theme.colorUI20};
        cursor: pointer;
      }
      svg {
        font-size: ${({ theme }) => theme.fontSize03};
        color: ${({ theme }) => theme.colorUI80};
      }
    }
  }

  .place-search-result {
    flex-grow: 1;
    flex-shrink: 0;
    width: 100%;
    display: flex;
    padding: ${({ theme }) => theme.spacing01};
    align-items: center;
    color: ${({ theme }) => theme.colorText50};
    .field-icon {
      color: ${({ theme }) => theme.colorPrimary70};
    }
    :hover {
      background: ${({ theme }) => theme.colorUI20};
      cursor: pointer;
      .field-icon-wrap {
        background: white;
      }
    }
  }
`;

export default function PlaceSearch({ onResultClick }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState('');
  const resultsRef = useRef();
  useOutsideClick([resultsRef], () => setIsOpen(false));

  useEffect(() => {
    if (error) {
      setError('');
    }
    const delayDebounceFn = setTimeout(() => {
      if (searchTerm.length > 2) {
        search();
      }
    }, 700);

    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  function onSearchClick() {
    if (searchTerm.length < 3) {
      setError(t`Location must have more than 3 characters`);
    } else {
      search();
    }
  }

  async function search() {
    try {
      const response = await axios.get(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${searchTerm}.json?`,
        {
          params: {
            access_token: process.env.REACT_APP_MAPBOX_TOKEN,
            types: ['place', 'address'],
          },
        },
      );
      setSearchResults(response.data.features);
      setIsOpen(true);
    } catch (error) {
      setError(t`Something went wrong when searching`);
    }
  }

  function handleLocationChange(event) {
    const isBackspace = (event.currentTarget.keyCode = 8);
    if (isOpen && isBackspace) {
      setIsOpen(false);
    }

    setSearchTerm(event.currentTarget.value);
  }

  return (
    <PlaceSearchWrap error={error}>
      <div className="place-search-search-area">
        <div className="place-search-input-area" style={{ position: 'relative', flexGrow: 1 }}>
          <Input
            placeholder={t`Eg. Leoben`}
            error={error}
            label={t`Location`}
            value={searchTerm}
            onChange={handleLocationChange}
          />
          {isOpen && (
            <div ref={(el) => (resultsRef.current = el)} className="place-search-results">
              <button onClick={() => setIsOpen(false)} className="place-search-results-exit-icon">
                <XIcon />
              </button>
              {searchResults.length > 0 ? (
                searchResults.map((result, index) => (
                  <li
                    key={index}
                    onClick={() => {
                      setIsOpen(false);
                      onResultClick(result);
                    }}
                    className="place-search-result"
                    tabIndex={index}
                  >
                    <div className="field-icon-wrap">
                      <FaMapMarkerAlt className="field-icon" />
                    </div>
                    {result.place_name}
                  </li>
                ))
              ) : (
                <div>{t`Could not find any results.`}</div>
              )}
            </div>
          )}
        </div>
        <ButtonPrimaryMedium onClick={onSearchClick}>{t`Search`}</ButtonPrimaryMedium>
      </div>
    </PlaceSearchWrap>
  );
}
